import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronDown, CreditCard, LogOut, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { logoutRemote } from '@/features/auth/services/auth-service'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'
import {
  formatMembershipPlan,
  formatMembershipStatus,
} from '@/features/billing/utils/membership-labels'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return (parts[0]?.[0] ?? '用') + (parts[1]?.[0] ?? '')
}

export function AuthUserMenu() {
  const navigate = useNavigate()
  const session = useAuthSessionStore((state) => state.session)
  const clearSession = useAuthSessionStore((state) => state.clearSession)
  const openMembershipDialog = useMembershipDialogStore((state) => state.openDialog)

  if (!session) return null

  const membershipLabel =
    formatMembershipPlan(session.membership?.planName, session.membership?.planCode) ||
    formatMembershipStatus(session.membership?.status)

  const handleLogout = async () => {
    await logoutRemote()
    clearSession()
    navigate({ to: '/login' })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 max-w-[11.5rem] gap-1.5 rounded-full px-2.5"
          aria-label={`${session.user.name}，${membershipLabel}`}
        >
          {session.user.avatarUrl ? (
            <img
              src={session.user.avatarUrl}
              alt={session.user.name}
              className="h-5 w-5 rounded-full object-cover"
            />
          ) : (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
              {getInitials(session.user.name)}
            </span>
          )}
          <span className="hidden min-w-0 max-w-[7.5rem] truncate text-sm font-medium sm:block">
            {session.user.name}
          </span>
          <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground sm:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="space-y-1">
            <div className="font-medium">{session.user.name}</div>
            <div className="text-xs font-normal text-muted-foreground">{session.user.email}</div>
            <div className="pt-1 text-xs font-normal text-muted-foreground">当前会员：{membershipLabel}</div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account">
            <User className="h-4 w-4" />
            个人中心
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={openMembershipDialog}>
          <CreditCard className="h-4 w-4" />
          开通会员
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout}>
          <LogOut className="h-4 w-4" />
          退出登录
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}